import { EnvironmentUrls } from 'Roblox';
import { authenticatedUser } from 'header-scripts';
import accountSettingsModule from '../accountSettingsModule';
import twoStepVerificationConstants from "./../constants/twoStepVerificationConstants";

function accountsService(
  $q,
  $log,
  httpService,
  formFieldsMappingService,
  languageResource,
  errorMessageService
) {
  'ngInject';

  const formServiceUrls = {
    getAccountInfoUrl: '/my/settings/json',
    accountSignoutUrl: '/authentication/signoutfromallsessionsandreauthenticate',
    accountUnblockUserUrl: '/userblock/unblockuser',
    accountAskParentToVerifyAge: '/my/account/AskParentVerifyAge',
    getAllowedDestinationTypes: '/account/settings/allowed-notification-destinations',
    getAccountSettingsGroups: '/account/settings/settings-groups',
    getAccountSubscriptionUrl: `${EnvironmentUrls.premiumFeaturesApi}/v1/users/{userid}/subscriptions`,

    // V2
    accountAppChatPrivacy: '/account/settings/app-chat-privacy',
    accountGameChatPrivacy: '/account/settings/game-chat-privacy',
    accountPrivateMessagePrivacy: '/account/settings/private-message-privacy',
    accountPrivateServerInvitePrivacy: '/account/settings/private-server-invite-privacy',
    accountFollowMePrivacy: '/account/settings/follow-me-privacy',
    accountAccountRestrictions: '/account/settings/account-restrictions',
    accountCountry: '/account/settings/account-country',
    accountPhone: `${EnvironmentUrls.accountInformationApi}/v1/phone`,
    accountPhoneDelete: `${EnvironmentUrls.accountInformationApi}/v1/phone/delete`,
    accountResendPhoneCode: `${EnvironmentUrls.accountInformationApi}/v1/phone/resend`,
    accountVerifyPhone: `${EnvironmentUrls.accountInformationApi}/v1/phone/verify`,
    getCountryListUrl: '/account/settings/countries',
    accountDescription: `${EnvironmentUrls.accountInformationApi}/v1/description`,
    accountBirthdate: `${EnvironmentUrls.accountInformationApi}/v1/birthdate`,
    accountGender: `${EnvironmentUrls.accountInformationApi}/v1/gender`,
    accountPromotionChannels: `${EnvironmentUrls.accountInformationApi}/v1/promotion-channels`,
    accountInformationMetadata: `${EnvironmentUrls.accountInformationApi}/v1/metadata`,
    accountChangeUsernameV2: `${EnvironmentUrls.accountInformationApi}/v1/username`,
    authenticationMetadata: `${EnvironmentUrls.accountInformationApi}/v1/metadata`,
    accountSettingsPolicy: `${EnvironmentUrls.universalAppConfigurationApi}/v1/behaviors/account-settings-ui/content`,

    // Two Step Verification
    getTwoStepVerificationConfiguration: `${EnvironmentUrls.twoStepVerificationApi}/v1/users/${authenticatedUser.id}/configuration`,
    enableEmailTwoStepVerification: `${EnvironmentUrls.twoStepVerificationApi}/v1/users/${authenticatedUser.id}/configuration/email/enable`,
    disableEmailTwoStepVerification: `${EnvironmentUrls.twoStepVerificationApi}/v1/users/${authenticatedUser.id}/configuration/email/disable`
  };

  const pinErrorMapper = errorMessageService.createErrorMapper(
    {
      // error messages based on ErrorCode Enum from ApiSites\Roblox.Api.Authentication\Roblox.Api.Authentication\Controllers\V1\AccountPinController.cs
      0: languageResource.get('Message.Error.System'),
      1: languageResource.get('Message.Error.NoPin'),
      2: languageResource.get('Message.Error.AccountLocked'),
      3: languageResource.get('Message.Error.TooManyRequests'),
      4: languageResource.get('Message.Error.IncorrectPin'),
      5: languageResource.get('Message.Error.AccountHasPin'),
      6: languageResource.get('Message.Error.NoVerifiedEmail'),
      7: languageResource.get('Message.Error.InvalidPinFormat')
    },
    languageResource.get('Message.Error.Default')
  );

  const phoneErrorMapper = errorMessageService.createErrorMapper(
    {
      // error messages based on ErrorCode Enum from ApiSites\Roblox.AccountInformation.Api\Roblox.AccountInformation.Api\Controllers\V1\PhoneInformationController.cs
      0: languageResource.get('Response.GeneralError'),
      1: languageResource.get('Response.FeatureDisabled'),
      2: languageResource.get('Response.InvalidPhoneTryAgain'),
      3: languageResource.get('Response.NumberAlreadyAssociated'),
      4: languageResource.get('Response.PinRequired'),
      5: languageResource.get('Response.IncorrectPasswordTryAgain'),
      6: languageResource.get('Response.IncorrectCodeTooManyTimes'),
      7: languageResource.get('Response.FeatureDisabled'),
      8: languageResource.get('Respones.InvalidCodePhone')
    },
    languageResource.get('Message.Error.Default')
  );

  const emailErrorMapper = errorMessageService.createErrorMapper(
    {
      // error messages based on EmailErrors Enum from ApiSites\Roblox.AccountSettings.Api\Roblox.AccountSettings.Api\ResponseEnums\EmailErrors.cs
      0: languageResource.get('Message.Error.Email.Unknown'),
      1: languageResource.get('Message.Error.Email.PinLocked'),
      2: languageResource.get('Message.Error.Email.FeatureDisabled'),
      3: languageResource.get('Message.Error.Email.TooManyAccounts'),
      4: languageResource.get('Message.Error.Email.SameEmail'),
      5: languageResource.get('Message.Error.Email.AlreadyVerified'),
      6: languageResource.get('Message.Error.Email.TooManyUpdates'),
      7: languageResource.get('Message.Error.Email.TooManyVerify'),
      8: languageResource.get('Message.Error.Email.IncorrectPassword'),
      9: languageResource.get('Message.Error.Email.InvalidEmail'),
      10: languageResource.get('Message.Error.Email.NoEmailAssociated')
    },
    languageResource.get('Message.Error.Default')
  );

  const twoStepVerificationErrorMapper = errorMessageService.createErrorMapper({
    [twoStepVerificationConstants.errorCodes.internal.tooManyRequests]: languageResource.get("Message.Error.TooManyRequests"),
    [twoStepVerificationConstants.errorCodes.internal.invalidEmail]: languageResource.get("Message.Error.NoVerifiedEmail"),
    [twoStepVerificationConstants.errorCodes.internal.invalidPassword]: languageResource.get("Message.Error.Email.IncorrectPassword"),
    [twoStepVerificationConstants.errorCodes.internal.featureDisabled]: languageResource.get("Response.FeatureDisabled"),
    [twoStepVerificationConstants.errorCodes.internal.pinLocked]: languageResource.get("Message.Error.Email.PinLocked")
  }, languageResource.get("Message.Error.Default"));

  const sendData = function(url, params, method, excludeCacheHeaders) {
    const urlConfig = {
      url,
      withCredentials: true
    };
    let data;
    switch (method) {
      case httpService.methods.get:
        urlConfig.noCache = !excludeCacheHeaders;
        return httpService.httpGet(urlConfig, params);
      case httpService.methods.post:
        urlConfig.headers = {
          'Content-Type': 'application/x-www-form-urlencoded'
        };
        if (params != null) {
          data = $.param(params);
        }
        return httpService.httpPost(urlConfig, data);
      case httpService.methods.patch:
        urlConfig.headers = {
          'Content-Type': 'application/x-www-form-urlencoded'
        };
        if (params != null) {
          data = $.param(params);
        }
        return httpService.httpPatch(urlConfig, data);
      case httpService.methods.delete:
        return httpService.httpDelete(urlConfig, params);
    }
  };

  const self = {
    setNotificationSettingsUrls(notificationsDomain) {
      formServiceUrls.getNotificationSettings = `${notificationsDomain}/v2/notifications/get-settings`;
      formServiceUrls.updateNotificationBandSettings = `${notificationsDomain}/v2/notifications/update-notification-settings`;
      formServiceUrls.removeDestinationTypeOptOut = `${notificationsDomain}/v2/notifications/receiver-destination-types/allow`;
      formServiceUrls.addDestinationTypeOptOut = `${notificationsDomain}/v2/notifications/receiver-destination-types/opt-out`;
    },

    setXboxConnectionUrls(authDomain) {
      formServiceUrls.xboxConnection = `${authDomain}/v1/xbox/connection`;
      formServiceUrls.disconnectXbox = `${authDomain}/v1/xbox/disconnect`;
    },

    setAccountSettingsApiUrl(accountSettingsApiDomain) {
      formServiceUrls.accountTradePrivacyUrl = `${accountSettingsApiDomain}/v1/trade-privacy`;
      formServiceUrls.accountTradeValueUrl = `${accountSettingsApiDomain}/v1/trade-value`;
      formServiceUrls.accountInventoryPrivacyUrl = `${accountSettingsApiDomain}/v1/inventory-privacy`;
      formServiceUrls.privacySettingsUrl = `${accountSettingsApiDomain}/v1/privacy`;
      formServiceUrls.privacySettingsInfoUrl = `${accountSettingsApiDomain}/v1/privacy/info`;
      formServiceUrls.getOrUpdateEmailUrl = `${accountSettingsApiDomain}/v1/email`;
      formServiceUrls.sendVerifyEmailUrl = `${accountSettingsApiDomain}/v1/email/verify`;
      formServiceUrls.accountSettingsApiDomain = accountSettingsApiDomain;
      formServiceUrls.accountConsumerThemesUrl = `${accountSettingsApiDomain}/v1/themes/user`;
      formServiceUrls.accountAvailableThemesUrl = `${accountSettingsApiDomain}/v1/themes/types`;
      formServiceUrls.updatePromotionChannelsUrl = `${accountSettingsApiDomain}/v1/promotion-channels`;
    },

    setAccountInformationUrl(accountInformationApiDomain) {
      formServiceUrls.accountDescription = `${accountInformationApiDomain}/v1/description`;
      formServiceUrls.accountBirthdate = `${accountInformationApiDomain}/v1/birthdate`;
      formServiceUrls.accountGender = `${accountInformationApiDomain}/v1/gender`;
      formServiceUrls.accountPromotionChannels = `${accountInformationApiDomain}/v1/promotion-channels`;
      formServiceUrls.accountInformationMetadata = `${accountInformationApiDomain}/v1/metadata`;
      formServiceUrls.accountPhone = `${accountInformationApiDomain}/v1/phone`;
      formServiceUrls.accountPhoneDelete = `${accountInformationApiDomain}/v1/phone/delete`;
      formServiceUrls.accountResendPhoneCode = `${accountInformationApiDomain}/v1/phone/resend`;
      formServiceUrls.accountVerifyPhone = `${accountInformationApiDomain}/v1/phone/verify`;
    },

    setAuthDomainUrl(authDomain) {
      formServiceUrls.accountPinUrl = `${authDomain}/v1/account/pin`;
      formServiceUrls.accountPinLockUrl = `${authDomain}/v1/account/pin/lock`;
      formServiceUrls.accountPinUnlockUrl = `${authDomain}/v1/account/pin/unlock`;
      formServiceUrls.connectedSocialAuthUrl = `${authDomain}/v1/social/connected-providers`;
      formServiceUrls.socialAuthDomainUrl = `${authDomain}/v1/social`;
      formServiceUrls.accountChangePasswordUrl = `${authDomain}/v2/user/passwords/change`;
      formServiceUrls.authenticationMetadata = `${authDomain}/v1/metadata`;
      formServiceUrls.accountChangeUsernameV2 = `${authDomain}/v1/username`;
    },

    setUniversalAppConfigurationUrl(universalAppConfigurationDomain) {
      formServiceUrls.accountSettingsPolicy = `${universalAppConfigurationDomain}/v1/behaviors/account-settings-ui/content`;
    },

    templateUrls: {
      passwordChangedUrl: 'user-account-password-changed.html',
      passwordErrorUrl: 'user-account-password-error.html',
      accountPhoneChangedUrl: 'user-account-phone-changed.html',
      accountVerifySmsSentUrl: 'user-account-verify-sms-sent.html',
      accountEmailChangedUrl: 'user-account-email-changed.html',
      accountVerifySentUrl: 'user-account-verify-sent.html',
      accountParentNeedNeedEmailUrl: 'user-account-parent-need-email.html',
      accountAgeChangeConfirmationUrl: 'user-account-age-change-confirmation.html',
      account2SvEnabledUrl: 'user-account-2sv-enabled.html',
      account2SvDisableConfirmationUrl: 'user-account-2sv-disable-confirmation.html'
    },

    // form population
    populateAccount: formFieldsMappingService,

    beginGetCountryList() {
      let params;
      let url;
      url = formServiceUrls.getCountryListUrl;
      return sendData(url, params, 'GET');
    },

    beginGetAccountInfo() {
      let url;
      let params;
      url = formServiceUrls.getAccountInfoUrl;
      return sendData(url, params, 'GET');
    },

    beginGetAccountSubscription() {
      let url;
      let params;
      url = formServiceUrls.getAccountSubscriptionUrl.replace(
        '{userid}',
        authenticatedUser.id
      );
      return sendData(url, params, 'GET');
    },

    beginGetAccountSettingsGroups() {
      let url;
      let params;
      url = formServiceUrls.getAccountSettingsGroups;
      return sendData(url, params, 'GET');
    },

    beginGetAllowedNotificationDestinationTypes() {
      let url;
      let params;
      url = formServiceUrls.getAllowedDestinationTypes;
      return sendData(url, params, 'GET');
    },

    beginAccountSignOut() {
      let url;
      let params;
      url = formServiceUrls.accountSignoutUrl;
      params = {
        __RequestVerificationToken: angular.element('[name=__RequestVerificationToken]').val()
      };
      return sendData(url, params, 'POST');
    },

    beginAccountChangePassword(passwordInfo) {
      let url;
      let params;
      url = formServiceUrls.accountChangePasswordUrl;
      params = {
        currentPassword: passwordInfo.currentPassword,
        newPassword: passwordInfo.newPassword
      };
      return sendData(url, params, 'POST');
    },

    beginAccountAddEmailAddress(emailInfo) {
      const url = formServiceUrls.getOrUpdateEmailUrl;
      const params = {
        emailAddress: emailInfo.emailAddress,
        password: emailInfo.password
      };
      return sendData(url, params, 'POST');
    },

    beginAccountVerifyEmailAddress() {
      const url = formServiceUrls.sendVerifyEmailUrl;
      return sendData(url, null, 'POST');
    },

    beginGetEmail() {
      const url = formServiceUrls.getOrUpdateEmailUrl;
      return sendData(url, null, 'GET');
    },

    beginGetPhone() {
      const url = formServiceUrls.accountPhone;

      return sendData(url, null, 'GET');
    },

    beginAccountUpdatePhone(phoneInfo) {
      const url = formServiceUrls.accountPhone;
      const params = {
        countryCode: phoneInfo.countryCode,
        prefix: phoneInfo.prefix,
        phone: phoneInfo.phone,
        password: phoneInfo.password
      };

      return sendData(url, params, 'POST');
    },

    beginAccountDeletePhone(deleteInfo) {
      const url = formServiceUrls.accountPhoneDelete;
      const params = {
        password: deleteInfo.password
      };

      return sendData(url, params, 'POST');
    },

    beginAccountResendPhoneCode() {
      const url = formServiceUrls.accountResendPhoneCode;
      return sendData(url, null, 'POST');
    },

    beginAccountVerifyPhone(codeInfo) {
      const url = formServiceUrls.accountVerifyPhone;
      const params = {
        code: codeInfo.code
      };
      return sendData(url, params, 'POST');
    },

    beginAccountUnblockUser(uid) {
      let url;
      let params;
      url = formServiceUrls.accountUnblockUserUrl;
      params = {
        blockeeId: uid
      };
      return sendData(url, params, 'POST');
    },

    beginAccountAskParentToVerifyAge() {
      let url;
      url = formServiceUrls.accountAskParentToVerifyAge;
      return sendData(url, null, 'POST');
    },

    beginGetAuthenticationMetadata() {
      const url = formServiceUrls.authenticationMetadata;
      return sendData(url, null, 'GET', true);
    },

    beginAccountChangeUsernameV2(user) {
      let url;
      let params;
      url = formServiceUrls.accountChangeUsernameV2;
      params = {
        username: user.username,
        password: user.password
      };
      return sendData(url, params, 'POST');
    },

    beginGetNotificationSettings() {
      const url = formServiceUrls.getNotificationSettings;
      return sendData(url, null, 'GET', true);
    },

    beginGet2svSettingV2() {
      return $q((resolve, reject) => {
        this.getTwoStepVerificationConfiguration().then(configuration => {
          let enabled = false;
          for (let i = 0; i < configuration.methods.length; i++) {
            let method = configuration.methods[i];
            if (method.mediaType.toLowerCase() === twoStepVerificationConstants.mediaTypes.email) {
              enabled = method.enabled;
              break;
            }
          }

          resolve(enabled);
        }).catch(reject);
      });
    },

    getTwoStepVerificationConfiguration() {
      return $q((resolve, reject) => {
        httpService.httpGet({
          url: formServiceUrls.getTwoStepVerificationConfiguration
        }).then(configuration => {
          resolve(configuration);
        }).catch(err => {
          let errorCode = httpService.getPrimaryApiErrorCode(err, twoStepVerificationConstants.errorCodes.api);
          reject({
            code: errorCode,
            message: twoStepVerificationErrorMapper.getErrorMessage(errorCode)
          });
        });
      });
    },

    enableEmailTwoStepVerification() {
      return $q((resolve, reject) => {
          httpService.httpPost({
            url: formServiceUrls.enableEmailTwoStepVerification
          }).then(() => {
            resolve({});
          }).catch(err => {
            let errorCode = httpService.getPrimaryApiErrorCode(err, twoStepVerificationConstants.errorCodes.api);
            reject({
              code: errorCode,
              message: twoStepVerificationErrorMapper.getErrorMessage(errorCode)
            });
          });
      });
    },

    disableTwoStepVerification(mediaType, password) {
      return $q((resolve, reject) => {
        switch (mediaType) {
          case twoStepVerificationConstants.mediaTypes.email:
            httpService.httpPost({
              url: formServiceUrls.disableEmailTwoStepVerification
            }, {
              password: password
            }).then(() => {
              resolve({});
            }).catch(err => {
              let errorCode = httpService.getPrimaryApiErrorCode(err, twoStepVerificationConstants.errorCodes.api);
              reject({
                code: errorCode,
                message: twoStepVerificationErrorMapper.getErrorMessage(errorCode)
              });
            });

            return;
          default:
            reject(twoStepVerificationConstants.errorCodes.internal.invalidMediaType);
        }
      });
    },

    beginUpdateNotificationSettings(
      updatedNotificationBandSettings,
      receiverDestinationTypeOptOuts,
      receiverDestinationTypeOptIns
    ) {
      const promises = [];
      // new specific (notificationband) settings
      const bandSettingsUrl = formServiceUrls.updateNotificationBandSettings;
      if (updatedNotificationBandSettings.length > 0) {
        promises.push(
          sendData(
            bandSettingsUrl,
            { updatedSettings: updatedNotificationBandSettings },
            'POST',
            true
          )
        );
      }

      // any updated destinationtype opt outs
      const allowDestinationUrl = formServiceUrls.removeDestinationTypeOptOut;
      angular.forEach(receiverDestinationTypeOptIns, function(value) {
        promises.push(sendData(allowDestinationUrl, { destinationType: value }, 'POST', true));
      });

      const optOutOfDestinationUrl = formServiceUrls.addDestinationTypeOptOut;
      angular.forEach(receiverDestinationTypeOptOuts, function(value) {
        promises.push(sendData(optOutOfDestinationUrl, { destinationType: value }, 'POST', true));
      });

      return $q.all(promises);
    },

    beginGetSocialConnected() {
      const url = formServiceUrls.connectedSocialAuthUrl;
      return sendData(url, null, 'GET', true);
    },

    beginUpdateSocialDisconnect(provider, password) {
      const url = `${formServiceUrls.socialAuthDomainUrl}/${provider}/disconnect`;

      const params = {};
      if (password) {
        params.password = password;
      }

      return sendData(url, params, 'POST');
    },

    beginGetAccountPinSetting() {
      const url = formServiceUrls.accountPinUrl;
      return sendData(url, null, 'GET', true);
    },

    beginCreateAccountPinSetting(newPin) {
      const url = formServiceUrls.accountPinUrl;
      const params = {
        pin: newPin
      };
      return sendData(url, params, 'POST', true);
    },

    beginDeleteAccountPinSetting() {
      const url = formServiceUrls.accountPinUrl;
      return sendData(url, null, 'DELETE', true);
    },

    beginUnlockAccountPinSetting(unlockPin) {
      const url = formServiceUrls.accountPinUnlockUrl;
      const params = {
        pin: unlockPin
      };
      return sendData(url, params, 'POST', true);
    },

    beginLockAccountPinSetting(unlockPin) {
      const url = formServiceUrls.accountPinLockUrl;
      return sendData(url, null, 'POST', true);
    },

    beginGetXboxConnection() {
      const url = formServiceUrls.xboxConnection;
      return sendData(url, null, 'GET', true);
    },

    beginDisconnectXbox() {
      const url = formServiceUrls.disconnectXbox;
      return sendData(url, null, 'POST');
    },

    // == V2 ==

    beginProcessErrorMessage(error) {
      let errorMsg = error || 'Something went wrong, please try again later.';
      if (error && error.errors && error.errors[0] && error.errors[0].message) {
        errorMsg = error.errors[0].message;
      }
      return errorMsg;
    },

    getPinErrorMessageFromCode(error) {
      return pinErrorMapper.getErrorMessageFromResponse(error);
    },

    getEmailErrorMessageFromCode(error) {
      return emailErrorMapper.getErrorMessageFromResponse(error);
    },

    getPhoneErrorMessageFromCode(error) {
      return phoneErrorMapper.getErrorMessageFromResponse(error);
    },

    // == Info ==

    // Metadata
    beginGetAccountInformationMetadata() {
      const url = formServiceUrls.accountInformationMetadata;
      return sendData(url, null, 'GET', true);
    },

    // Description
    beginGetDescription() {
      const url = formServiceUrls.accountDescription;
      return sendData(url, null, 'GET', true);
    },

    beginUpdateDescription(description) {
      const url = formServiceUrls.accountDescription;
      const params = {
        description
      };
      return sendData(url, params, 'POST');
    },

    // Birthday
    beginGetBirthdate() {
      const url = formServiceUrls.accountBirthdate;
      return sendData(url, null, 'GET', true);
    },

    beginUpdateBirthdate(birthdate) {
      const url = formServiceUrls.accountBirthdate;
      const params = {
        birthDay: birthdate.birthDay,
        birthMonth: birthdate.birthMonth,
        birthYear: birthdate.birthYear
      };
      return sendData(url, params, 'POST');
    },

    // Gender
    beginGetGender() {
      const url = formServiceUrls.accountGender;
      return sendData(url, null, 'GET', true);
    },

    beginUpdateGender(gender) {
      const url = formServiceUrls.accountGender;
      const params = {
        gender
      };
      return sendData(url, params, 'POST');
    },

    beginGetCountry() {
      const url = formServiceUrls.accountCountry;
      return sendData(url, null, 'GET', true);
    },

    beginUpdateCountry(personal) {
      const url = formServiceUrls.accountCountry;
      const params = {
        countryId: personal.country.countryId
      };
      return sendData(url, params, 'POST');
    },

    // Themes
    beginGetConsumerThemes() {
      const url = formServiceUrls.accountConsumerThemesUrl;
      return sendData(url, null, 'GET', true);
    },

    beginSetConsumerThemes(themeType) {
      const url = formServiceUrls.accountConsumerThemesUrl;
      const params = {
        themeType
      };
      return sendData(url, params, 'PATCH');
    },

    beginGetAvailableThemeTypes() {
      const url = formServiceUrls.accountAvailableThemesUrl;
      return sendData(url, null, 'GET', true);
    },

    // Promotion channels
    beginGetPromotionChannels() {
      const url = formServiceUrls.accountPromotionChannels;
      return sendData(url, null, 'GET', true);
    },

    beginUpdatePromotionChannels(updatedInfo) {
      const url = formServiceUrls.accountPromotionChannels;
      const params = {
        facebook: updatedInfo.FacebookUrl,
        twitter: updatedInfo.TwitterUrl,
        youtube: updatedInfo.YouTubeUrl,
        twitch: updatedInfo.TwitchUrl,
        promotionChannelsVisibilityPrivacy: updatedInfo.SocialNetworksVisibilityPrivacy
      };
      return sendData(url, params, 'POST');
    },

    // == accountSecurity ==

    beginGetAccountRestrictions() {
      const url = formServiceUrls.accountAccountRestrictions;
      return sendData(url, null, 'GET', true);
    },
    beginUpdateAccountRestrictions(flag) {
      const url = formServiceUrls.accountAccountRestrictions;
      const params = {
        isEnabled: flag
      };
      return sendData(url, params, 'POST', true);
    },

    // == accountSecurity ==

    // == accountPrivacy ==

    beginGetContactSettings() {
      const appChatPrivacyUrl = formServiceUrls.accountAppChatPrivacy;
      const gameChatPrivacyUrl = formServiceUrls.accountGameChatPrivacy;
      const priateMessagePrivacyUrl = formServiceUrls.accountPrivateMessagePrivacy;

      const promises = {
        appChatPrivacy: sendData(appChatPrivacyUrl, null, 'GET'),
        gameChatPrivacy: sendData(gameChatPrivacyUrl, null, 'GET'),
        privateMessagePrivacy: sendData(priateMessagePrivacyUrl, null, 'GET')
      };

      return $q.all(promises).then(function(data) {
        const contactSettings = {};
        contactSettings.appChatPrivacy = data.appChatPrivacy.AppChatPrivacy;
        contactSettings.gameChatPrivacy = data.gameChatPrivacy.GameChatPrivacy;
        contactSettings.privateMessagePrivacy = data.privateMessagePrivacy.PrivateMessagePrivacy;
        return contactSettings;
      });
    },

    // App Chat Privacy
    beginGetAppChatPrivacy() {
      const url = formServiceUrls.accountAppChatPrivacy;
      return sendData(url, null, 'GET', true);
    },
    beginUpdateAppChatPrivacy(contactSettings) {
      const url = formServiceUrls.accountAppChatPrivacy;
      const params = {
        AppChatPrivacy: contactSettings.AppChatPrivacy
      };
      return sendData(url, params, 'POST');
    },

    // Game Chat Privacy
    beginGetGameChatPrivacy() {
      const url = formServiceUrls.accountGameChatPrivacy;
      return sendData(url, null, 'GET', true);
    },
    beginUpdateGameChatPrivacy(contactSettings) {
      const url = formServiceUrls.accountGameChatPrivacy;
      const params = {
        GameChatPrivacy: contactSettings.GameChatPrivacy
      };
      return sendData(url, params, 'POST');
    },

    // Privacy Settings
    beginGetPrivacySettingsInfo() {
      const url = formServiceUrls.privacySettingsInfoUrl;
      return sendData(url, null, 'GET', true);
    },
    beginGetPrivacySettings() {
      const url = formServiceUrls.privacySettingsUrl;
      return sendData(url, null, 'GET', true);
    },
    beginUpdatePrivacySettings(contactSettings) {
      const url = formServiceUrls.privacySettingsUrl;
      const params = {
        phoneDiscovery: contactSettings.PhoneDiscoveryPrivacy
      };
      return sendData(url, params, 'PATCH');
    },

    // Private Message Privacy
    beginGetPrivateMessagePrivacy() {
      const url = formServiceUrls.accountPrivateMessagePrivacy;
      return sendData(url, null, 'GET', true);
    },
    beginUpdatePrivateMessagePrivacy(contactSettings) {
      const url = formServiceUrls.accountPrivateMessagePrivacy;
      const params = {
        PrivateMessagePrivacy: contactSettings.PrivateMessagePrivacy
      };
      return sendData(url, params, 'POST');
    },

    // Private Server Invite Privacy
    beginGetPrivateServerInvitePrivacy() {
      const url = formServiceUrls.accountPrivateServerInvitePrivacy;
      return sendData(url, null, 'GET', true);
    },
    beginUpdatePrivateServerInvitePrivacy(joinSettings) {
      const url = formServiceUrls.accountPrivateServerInvitePrivacy;
      const params = {
        PrivateServerInvitePrivacy: joinSettings.privateServerInvitePrivacy
      };
      return sendData(url, params, 'POST');
    },

    // Follow Me Privacy
    beginGetFollowMePrivacy() {
      const url = formServiceUrls.accountFollowMePrivacy;
      return sendData(url, null, 'GET', true);
    },

    beginUpdateFollowMePrivacy(joinSettings) {
      const url = formServiceUrls.accountFollowMePrivacy;
      const params = {
        FollowMePrivacy: joinSettings.followMePrivacy
      };
      return sendData(url, params, 'POST');
    },

    // Inventory Privacy
    beginGetInventoryPrivacy() {
      const url = formServiceUrls.accountInventoryPrivacyUrl;
      return sendData(url, null, 'GET', true);
    },

    beginUpdateInventoryPrivacy(inventorySettings) {
      const url = formServiceUrls.accountInventoryPrivacyUrl;
      const params = {
        inventoryPrivacy: inventorySettings.inventoryPrivacy
      };
      return sendData(url, params, 'POST');
    },

    getUpdateTradePrivacyMessageWhenConflict() {
      return languageResource.get('Response.Dialog.UpdateTradeSetting');
    },

    // Trade Privacy
    beginGetTradePrivacy() {
      const url = formServiceUrls.accountTradePrivacyUrl;
      return sendData(url, null, 'GET', true);
    },

    beginUpdateTradePrivacy(tradeSettings) {
      const url = formServiceUrls.accountTradePrivacyUrl;
      const params = {
        tradePrivacy: tradeSettings.tradePrivacy
      };
      return sendData(url, params, 'POST');
    },

    getUpdateInventoryPrivacyMessageWhenConflict() {
      return languageResource.get('Response.Dialog.UpdateInventorySetting');
    },

    // Trade Value
    beginGetTradeValue() {
      const url = formServiceUrls.accountTradeValueUrl;
      return sendData(url, null, 'GET', true);
    },

    beginUpdateTradeValue(tradeSettings) {
      const url = formServiceUrls.accountTradeValueUrl;
      const params = {
        tradeValue: tradeSettings.tradeValue
      };
      return sendData(url, params, 'POST');
    },

    // == billing ==

    getPaymentRecords(url) {
      return sendData(url, null, 'GET', true);
    },

    beginGetAccountSettingsPolicy() {
      const url = formServiceUrls.accountSettingsPolicy;
      return sendData(url, null, 'GET', true);
    }

    // == V2 ==
  };

  return self;
}

accountSettingsModule.factory('accountsService', accountsService);

export default accountsService;
